/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 바디 클래스를 정의한다.
 *
 * @file /src/models/table/Body.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 15.
 */
import { Table } from '@models/table/Table';
import { Record } from '@models/store/Record';
import { Group } from '@models/table/Group';
import { Row } from '@models/table/Row';
import { Summary } from '@models/table/Summary';
import { Menu } from '@components/menu/Menu';
import { Language, LanguageText } from '@utils/Language';
import { Dom } from '@utils/Dom';
import { DomList } from '@utils/DomList';
import { Html } from '@utils/Html';
import './Body.css';

export interface Properties {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 빈 데이터 메시지
     */
    emptyText?: LanguageText;

    /**
     * 행 포커스 가능여부
     */
    focusableRow?: boolean;

    /**
     * 컬럼 포커스 가능여부
     */
    focusableCell?: boolean;

    /**
     * 행 지연렌더링 여부
     */
    isLazyRender?: boolean;
}

export class Body {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 그룹목록
     */
    groups: Map<string, Group> = new Map();

    /**
     * 그룹인덱스
     */
    groupIndexes: string[] = [];

    /**
     * 행
     */
    rows: Map<string, Row> = new Map();

    /**
     * 행 포커스 가능여부
     */
    focusableRow: boolean;

    /**
     * 셀 포커스 가능여부
     */
    focusableCell: boolean;

    /**
     * 현재 포커스중인 셀
     */
    focusedCell: { recordId: string; columnIndex: number } = {
        recordId: null,
        columnIndex: null,
    };

    /**
     * 빈 데이터 메시지
     */
    emptyText: LanguageText;

    /**
     * 행 lazyRender 여부
     */
    isLazyRender: boolean = false;

    /**
     * 가변높이 테이블의 렌더 직전 높이 캐시
     */
    cachedContentHeight: number | null = null;

    /**
     * 편집중인 컬럼
     */
    editingColumn: { row: Row; columnIndex: number } = null;

    /**
     * 바디 DOM
     */
    $body: Dom = null;

    /**
     * 바디 DOM
     */
    $freezeCover: Dom = null;

    /**
     * 합계
     */
    summary: Summary;

    /**
     * 트리인경우 확장된 행의 레코드고유값
     */
    expanded: Map<string, boolean> = new Map();

    /**
     * 테이블바디 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        this.table = properties.table;

        this.emptyText = properties.emptyText ?? null;
        this.focusableRow = properties.focusableRow !== false;
        this.focusableCell = properties.focusableCell !== false;
        this.isLazyRender = properties.isLazyRender === true;
        this.summary = new Summary(this.table);
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 레코드로 ROW 객체를 가져온다.
     *
     * @param record - 레코드
     * @returns 행
     */
    getRow(record: Record): Row {
        if (record === null) {
            return null;
        }

        if (this.rows.has(record.getId()) == false) {
            const row = new Row({ table: this.table, record: record });
            if (this.getTable().getSelection().isSelected(record) == true) {
                row.$getRow().addClass('select');
            }
            this.rows.set(record.getId(), row);
        }

        return this.rows.get(record.getId());
    }

    /**
     * 레코드고유값으로 ROW 객체를 가져온다.
     *
     * @param id - 레코드고유값
     * @returns 행
     */
    getRowById(id: string): Row {
        return this.rows.get(id) ?? null;
    }

    /**
     * 바디 DOM을 가져온다.
     *
     * @returns Body 레이아웃 DOM
     */
    $getBody(): Dom {
        if (this.$body === null) {
            this.$body = Html.create('div', { 'data-model': 'table', 'data-role': 'body' });
            this.$body.setStyleProperty('anchor-name', '--aui-table-body-' + this.getTable().getId());

            this.$body.on('pointerdown', async (_, e) => {
                const $column = Html.el(e.target).getParents('div[data-model=table][data-role=column]');
                if ($column === null) {
                    return;
                }

                const $row = $column.getParents('div[data-role=row]');
                if ($row === null) {
                    return;
                }

                const row = this.getRowById($row.getAttr('data-record-id'));
                const index = parseInt($column.getAttr('data-index'), 10);
                await this.focusCell(row, index);
            });

            this.$body.on('click', async (_, e: PointerEvent) => {
                const $column = Html.el(e.target).getParents('div[data-model=table][data-role=column]');
                if ($column === null) {
                    return;
                }

                /**
                 * ::after(편집가능 배지)는 anchor-positioning 으로 컬럼 영역 밖에 그려지므로,
                 * 클릭 좌표가 컬럼 자신의 사각형 밖에 있다면 배지가 클릭된 것으로 판단한다.
                 */
                const rect = $column.getEl().getBoundingClientRect();
                const isBadgeClick =
                    e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom;
                if (isBadgeClick == true) {
                    console.log('editable badge(::after) clicked', $column.getEl());
                }

                const $row = $column.getParents('div[data-role=row]');
                if ($row === null) {
                    return;
                }

                const row = this.getRowById($row.getAttr('data-record-id'));
                const index = parseInt($column.getAttr('data-index'), 10);
                const column = this.getTable().getColumnAt(index) ?? null;

                /**
                 * 컬럼이 행 선택이 가능한경우, 행을 선택한다.
                 */
                if (column.isSelectable() == true) {
                    const changed = await this.#selectRow(row.getRecord().getId(), e);
                    await this.getTable().fireEvent('rowClick', [row, changed, this.getTable()]);
                } else {
                    await this.getTable().fireEvent('rowClick', [row, false, this.getTable()]);
                }
            });

            this.$body.on('dblclick', async (_, e) => {
                const $column = Html.el(e.target).getParents('div[data-model=table][data-role=column]');
                if ($column === null) {
                    return;
                }

                const $row = $column.getParents('div[data-role=row]');
                if ($row === null) {
                    return;
                }

                const row = this.getRowById($row.getAttr('data-record-id'));
                const index = parseInt($column.getAttr('data-index'), 10);
                const column = this.getTable().getColumnAt(index) ?? null;

                if (index == -1 || column == null) {
                    return;
                }

                if (column.isEditable() == true) {
                    await this.onEdit(row, index);
                    return;
                }

                if (this.getTable().hasEvent('openItem') == false) {
                    return;
                }

                if (this.getTable().getSelection().isSelectable() == true) {
                    await this.getTable().getSelection().select(row.getRecord(), false);
                }

                this.getTable().fireEvent('openItem', [row.getRecord(), this.getTable(), column.dataIndex, index]);
            });

            this.$body.on('contextmenu', async (_, e) => {
                if (this.getTable().hasEvent('openMenu') == false) {
                    return;
                }

                const $column = Html.el(e.target).getParents('div[data-model=table][data-role=column]');
                if ($column === null) {
                    return;
                }

                const $row = $column.getParents('div[data-role=row]');
                if ($row === null) {
                    return;
                }

                if ((this.getTable().listeners.openMenu ?? []).length > 0) {
                    e.preventDefault();

                    /**
                     * 컬럼에 메뉴의 기준위치 DOM 을 추가한다.
                     */
                    const row = this.getRowById($row.getAttr('data-record-id'));
                    const record = row.getRecord();

                    let selections: Record[] = [record];
                    if (this.getTable().getSelection().isSelected(record.getId()) == true) {
                        selections = this.getTable().getSelection().getSelections();
                    } else {
                        if (this.getTable().getSelection().isSelectable() == true) {
                            await this.getTable().getSelection().deselectAll();
                            await this.getTable().getSelection().restoreSelections([record], false, true);
                            selections = this.getTable().getSelection().getSelections();
                        } else {
                            $row.addClass('select');
                        }
                    }

                    const menu = new Menu({
                        removeOnHide: true,
                        listeners: {
                            show: () => {
                                this.getTable().getScroller()?.preventScroll('x', true);
                                this.getTable().getScroller()?.preventScroll('y', true);
                            },
                            hide: () => {
                                this.getTable().getScroller()?.preventScroll('x', false);
                                this.getTable().getScroller()?.preventScroll('y', false);
                                if (this.getTable().getSelection().isSelectable() == false) {
                                    $row.removeClass('select');
                                }
                            },
                        },
                    });

                    await this.getTable().fireEvent('openMenu', [menu, selections, this.getTable()]);
                    menu.showAt(e.clientX, e.clientY);
                    e.preventDefault();
                }
            });
        }

        return this.$body;
    }

    /**
     * 고정열 뒤를 덮을 고정열커버 DOM 을 가져온다.
     *
     * @returns 고정열커버 DOM
     */
    $getFreezeCover(): Dom {
        if (this.$freezeCover == null) {
            this.$freezeCover = Html.create('div', { 'data-model': 'table', 'data-role': 'freeze-cover' });
            const id = this.getTable().getId();
            this.$freezeCover.setStyleProperty('position-anchor', '--aui-table-header-freeze-' + id);
            /**
             * 높이는 "실제 데이터 높이(body)"와 "화면에 보이는 body 영역 높이" 중 작은 값으로 잡는다.
             * "화면에 보이는 body 영역 높이"는 content 의 보이는 높이에서 header 높이를 뺀 값이다
             * (content 안에 header+body 가 같이 있고 header 는 sticky 로 항상 보이니까).
             * 예전엔 overflow 클리핑에 의존해서 화면 밖으로 넘친 부분이 잘렸지만,
             * 지금은 앵커들을 직접 계산해서 min() 으로 같은 효과를 낸다 (데이터가 적으면 body 기준, 넘치면 보이는 영역 기준).
             */
            this.$freezeCover.setStyleProperty(
                'height',
                'min(anchor-size(--aui-table-body-' +
                    id +
                    ' height), calc(anchor-size(--aui-table-content-' +
                    id +
                    ' height) - anchor-size(--aui-table-header-freeze-' +
                    id +
                    ' height)))'
            );
        }

        return this.$freezeCover;
    }

    /**
     * 컬럼을 고정한다.
     */
    setFreeze(): void {
        this.rows.forEach((row) => {
            row.setFreeze();
        });

        for (const group of this.groups.values()) {
            group.setFreeze();
        }

        this.summary.setFreeze();
    }

    /**
     * 행에 포커스를 지정한다.
     *
     * @param row - 행
     * @param is_animate - 부드러운 스크롤 여부
     * @param focused - 컴포넌트에 포커스를 실제로 지정할지 여부
     */
    async focusRow(row: Row, is_animate: boolean = true, focused: boolean = true): Promise<void> {
        if (focused == true) {
            this.getTable().$getComponent().focus();
        }

        if (row !== null) {
            const $record = row.$getRow();

            if (this.focusedCell.recordId !== null) {
                const row = this.getRowById(this.focusedCell.recordId);
                if (row !== null) {
                    row.$getRow().removeClass('focus');
                }
                Html.get('div[data-role=row].focus', this.$getBody())?.removeClass('focus');
            }

            this.focusedCell.recordId = row.getRecord().getId();
            $record.addClass('focus');

            const $target = this.getTable().$getContent();
            const position = $record.getPosition($target);
            const scroll = $target.getScroll();

            const elementTop = position.top;
            const elementBottom = position.top + $record.getOuterHeight();

            let viewTop = 0;
            let viewBottom = $target.getInnerHeight();

            const topPadding = (this.getTable().getHeader()?.getHeight() ?? 0) + 1;
            const bottomPadding = this.getTable().getSelection().$getSelectionView().getHeight() + 1;

            let top = null;
            if (elementTop < viewTop + topPadding) {
                top = scroll.top + (elementTop - viewTop) - topPadding;
            } else if (elementBottom > viewBottom - bottomPadding) {
                top = scroll.top + (elementBottom - viewBottom) + bottomPadding;
            }

            if (top !== null) {
                $target.getEl().scrollTo({
                    top: top,
                    behavior: is_animate == true ? 'smooth' : 'instant',
                });
            }
        }
    }

    /**
     * 셀에 포커스를 지정한다.
     *
     * @param row - 행
     * @param columnIndex - 컬럼 인덱스
     * @param focused - 컴포넌트에 포커스를 실제로 지정할지 여부
     */
    async focusCell(row: Row, columnIndex: number, focused: boolean = true): Promise<void> {
        if (focused == true) {
            this.getTable().$getComponent().focus();
        }

        if (row !== null) {
            await this.focusRow(row, true, focused);
            this.focusedCell.recordId = row.getRecord().getId();
            if (this.focusableCell === false) {
                return;
            }

            const $record = row.$getRow();
            const $column = Html.get(
                'div[data-model=table][data-role=column][data-index="' + columnIndex + '"]',
                $record
            );
            if ($column !== null) {
                Html.get('div[data-model=table][data-role=column].focus', this.$getBody())?.removeClass('focus');
                $column.addClass('focus');
                this.focusedCell.columnIndex = columnIndex;

                if ($column.getParent().is('div[data-model=table][data-role=freeze]') == false) {
                    const $target = this.getTable().$getContent();
                    const position = $column.getPosition($target);
                    const scroll = $target.getScroll();

                    const elementLeft = position.left;
                    const elementRight = position.left + $column.getOuterWidth();
                    const elementTop = position.top;
                    const elementBottom = position.top + $record.getOuterHeight();

                    let viewLeft = 0;
                    let viewRight = $target.getInnerWidth();
                    let viewTop = 0;
                    let viewBottom = $target.getInnerHeight();

                    const leftPadding = (this.getTable().getHeader()?.$getFreeze()?.getWidth() ?? 0) + 3;
                    const rightPadding = 1;
                    const topPadding = (this.getTable().getHeader()?.getHeight() ?? 0) + 1;
                    const bottomPadding = this.getTable().getSelection().$getSelectionView().getHeight() + 1;

                    let left = null;
                    if (elementLeft <= viewLeft + leftPadding) {
                        left = scroll.left + (elementLeft - viewLeft) - leftPadding;
                    } else if (elementRight > viewRight - rightPadding) {
                        left = scroll.left + (elementRight - viewRight) + rightPadding;
                    }

                    let top = null;
                    if (elementTop < viewTop + topPadding) {
                        top = scroll.top + (elementTop - viewTop) - topPadding;
                    } else if (elementBottom > viewBottom - bottomPadding) {
                        top = scroll.top + (elementBottom - viewBottom) + bottomPadding;
                    }

                    if (left !== null) {
                        $target.getEl().scrollTo({
                            top: top ?? scroll.top,
                            left: left,
                            behavior: 'smooth',
                        });
                    }
                }
            }
        }
    }

    /**
     * 셀 포커스를 해제한다.
     */
    async blurCell(): Promise<void> {
        const row = this.getRowById(this.focusedCell.recordId);
        if (row !== null) {
            row.$getRow().removeClass('focus');
        }
        Html.get('div[data-model=table][data-role=column].focus', this.$getBody())?.removeClass('focus');

        this.focusedCell.recordId = null;
        this.focusedCell.columnIndex = null;
    }

    /**
     * 화면상에 보이는 ROW 목록을 가져온다.
     *
     * @returns 화면상에 보이는 ROW 목록
     */
    $getVisibleRows(): DomList {
        if (this.getTable().isTree() == false) {
            return Html.all('div[data-role=row]', this.$getBody());
        } else {
            const rows: HTMLElement[] = [];
            const traverseExpandedBranches = ($parent: Dom<HTMLElement>) => {
                const $row = Html.get('> div[data-role=row]', $parent);
                rows.push($row.getEl());

                if ($parent.getAttr('data-role') == 'tree' && $parent.hasClass('expand') == true) {
                    const $children = Html.all('> div[data-role=branch] > div[data-role]', $parent);
                    $children.forEach(($child) => {
                        traverseExpandedBranches($child);
                    });
                }
            };

            Html.all('div[data-role=group] > div[data-role=tree]', this.$getBody()).forEach(($root) => {
                traverseExpandedBranches($root);
            });

            return new DomList<HTMLElement>(rows);
        }
    }

    /**
     * 포커스셀을 이동한다.
     *
     * @param direction - 이동할 방향
     * @param focused - 컴포넌트에 포커스를 실제로 지정할지 여부
     */
    async moveFocusCell(direction: string, focused: boolean = true): Promise<void> {
        const $rows = this.$getVisibleRows();
        const recordIds = $rows.getAttrs('data-record-id');
        const recordId = this.focusedCell.recordId ?? null;
        let row = recordId !== null ? this.getRowById(recordId) : null;

        let columnIndex = this.focusedCell.columnIndex ?? 0;

        switch (direction) {
            case 'up':
                if (recordId === null) return;
                row = this.getRowById(recordIds[recordIds.indexOf(recordId) - 1]);
                break;

            case 'down':
                if (recordId === null) {
                    row = this.getRowById(recordIds[0] ?? null);
                } else {
                    row = this.getRowById(recordIds[recordIds.indexOf(recordId) + 1]);
                }
                break;

            case 'left': {
                if (row === null) return;
                const minIndex =
                    Html.get('div[data-model=table][data-role=column][data-index="-1"]', row.$getRow()) !== null
                        ? -1
                        : 0;
                columnIndex = Math.max(columnIndex - 1, minIndex);
                break;
            }

            case 'right':
                columnIndex = Math.min(columnIndex + 1, this.getTable().getColumns().length - 1);
                break;
        }

        this.focusCell(row, columnIndex, focused);
    }

    /**
     * 로드된 자식이 존재하는 행을 자동으로 확장한다.
     */
    async autoExpand(): Promise<void> {
        if (this.getTable().isTree() == false) {
            return;
        }

        this.rows.forEach(async (row) => {
            await row.expand(true, false);
        });
    }

    /**
     * 트리가 다시 로드될때 확장/축소행을 복원한다.
     *
     * @returns 복원여부
     */
    async restoreExpand(): Promise<boolean> {
        if (this.expanded.size > 0) {
            this.expanded.forEach(async (expanded, record_id) => {
                const record = this.getTable().getStore().get(record_id);
                if (record === null) {
                    return;
                }

                const row = this.getRow(record);
                if (expanded == true) {
                    await row.expand(false, true);
                } else {
                    await row.collapse();
                }
            });

            return true;
        }

        return false;
    }

    /**
     * 전체 트리를 확장한다.
     *
     * @param expandedDepth - 확장을 제한할 최대 깊이 (true 인 경우 모든 행을 확장한다.)
     */
    async expandAll(expandedDepth: boolean | number = true): Promise<void> {
        if (this.getTable().isTree() == false) {
            return;
        }

        this.rows.forEach(async (row) => {
            await row.expand(expandedDepth, true);
        });
    }

    /**
     * 현재 편집모드인지 확인한다.
     *
     * @returns 편집모드여부
     */
    isEditing(): boolean {
        return this.editingColumn !== null;
    }

    /**
     * 데이터를 렌더링한다.
     *
     * @param is_reset - 기존 데이터 초기화여부
     */
    async render(is_reset: boolean = true): Promise<void> {
        /**
         * 테이블이 가변높이일 경우, 데이터를 다시 불러오기전까지 현재 높이를 가급적 유지한다.
         */
        let fixedHeight: number = null;
        if (
            this.getTable().isRendered() == true &&
            this.getTable().getLayout() == 'auto' &&
            this.getTable().height === null &&
            this.cachedContentHeight !== null
        ) {
            fixedHeight = this.cachedContentHeight;
            this.getTable()
                .$getContent()
                .setStyle('height', fixedHeight + 'px');
        }
        this.$getBody().empty();

        if (this.emptyText !== null) {
            this.$getBody().setAttr('data-empty-text', await Language.resolveText(this.emptyText));
        }

        if (is_reset == true) {
            this.groups.forEach((group) => {
                group.destroy();
            });
            this.groups.clear();

            this.rows.forEach((row) => {
                row.destroy();
            });
            this.rows.clear();

            this.summary.destroy();
            this.summary = new Summary(this.table);
        }

        for (const groupId of this.getTable().getStore().getGroupIds()) {
            if (this.groups.has(groupId) == false) {
                this.groups.set(
                    groupId,
                    new Group({
                        table: this.table,
                        dataIndexValue: groupId,
                    })
                );
            }
            this.groups.get(groupId).setRecords(this.getTable().getStore().getGroup(groupId).getRecordIds());
            this.$getBody().append(this.groups.get(groupId).$getGroup());
            await this.groups.get(groupId).render();
        }

        if (this.getTable().getStore().getGroupIds().length !== 0) {
            if (this.getTable().summary == true) {
                this.$getBody().append(this.summary.$getSummary(this.getTable().getStore().getRecords()));
            }
        }

        this.$getBody().append(this.$getFreezeCover());

        if (fixedHeight !== null) {
            this.getTable().$getContent().setStyle('height', null);
        }

        if (this.getTable().isRendered() == true) {
            this.getTable().getScroller()?.updatePosition();
        }

        if (this.getTable().getLayout() == 'auto' && this.getTable().height === null) {
            requestAnimationFrame(() => {
                this.cachedContentHeight = this.getTable().$getContent().getHeight();
            });
        }
    }

    /**
     * 셀 편집모드에 진입한다.
     *
     * @param row - 셀이 속한 행
     * @param column - 셀
     * @param $column - 셀 DOM
     */
    async onEdit(row: Row, columnIndex: number): Promise<void> {
        if (this.isEditing() == true) {
            return;
        }

        if (this.getTable().isEditorDisabled() == true) {
            return;
        }

        this.getTable().getScroller().preventScroll('x', true);
        this.getTable().getScroller().preventScroll('y', true);
        this.getTable().$getComponent().addClass('editing');

        this.editingColumn = { row, columnIndex };
        const column = this.getTable().getColumnAt(columnIndex) ?? null;
        const $column = Html.get(
            'div[data-model=table][data-role=column][data-index="' + columnIndex + '"]',
            row.$getRow()
        );

        if ($column !== null) {
            await this.focusCell(row, columnIndex);
            await column.onEdit(row, $column);

            if (column.editingField === null) {
                this.getTable().getScroller().preventScroll('x', false);
                this.getTable().getScroller().preventScroll('y', false);
                this.getTable().$getComponent().removeClass('editing');
                this.editingColumn = null;
            }
        }
    }

    /**
     * 편집을 종료한다.
     *
     * @param commit - 입력사항을 적용할지 여부
     */
    async onEdited(commit: boolean): Promise<void> {
        if (this.isEditing() == false) {
            return;
        }

        const column = this.getTable().getColumnAt(this.editingColumn.columnIndex) ?? null;
        if (column.editingField === null) {
            return;
        }

        if (commit == true) {
            const value = await column.editingField.getValue();
            await this.editingColumn.row.getRecord().set(column.dataIndex, value);
        }

        this.getTable().getScroller().preventScroll('x', false);
        this.getTable().getScroller().preventScroll('y', false);
        this.getTable().$getComponent().removeClass('editing');

        await this.focusCell(this.editingColumn.row, this.editingColumn.columnIndex);
        this.editingColumn = null;
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        if (e.key.startsWith('Arrow') == true) {
            this.moveFocusCell(e.key.replace('Arrow', '').toLowerCase());
            e.preventDefault();
            return false;
        }

        if (e.key === 'Enter' && e.repeat == false) {
            this.onEnter(e);
        }

        if (e.key === ' ' && e.repeat == false) {
            this.onSpace(e);
        }

        if (e.key === 'Escape' && e.repeat == false) {
            this.onEsc(e);
            //this.getTable().getSelection().deselectAll();
        }

        return true;
    }

    /**
     * Enter키를 입력했을 때 처리한다.
     *
     * @param e - 키보드 이벤트
     */
    async onEnter(e: KeyboardEvent): Promise<void> {
        if (this.focusedCell.recordId === null) {
            return;
        }

        if (this.focusedCell.columnIndex !== null) {
            const column = this.getTable().getColumnAt(this.focusedCell.columnIndex);
            if (column.isEditable() == true) {
                if (this.isEditing() == false) {
                    await this.onEdit(this.getRowById(this.focusedCell.recordId), this.focusedCell.columnIndex);
                    e.stopPropagation();
                    return;
                }
            }
        }

        const row = this.getRowById(this.focusedCell.recordId);
        const changed = await this.#selectRow(this.focusedCell.recordId, true);
        await this.getTable().fireEvent('rowClick', [row, changed, this.getTable()]);
    }

    /**
     * Space키를 입력했을 때 처리한다.
     *
     * @param e - 키보드 이벤트
     */
    async onSpace(e: KeyboardEvent): Promise<void> {
        if (this.focusedCell.recordId === null) {
            return;
        }

        this.#selectRow(this.focusedCell.recordId, true);

        e.preventDefault();
        e.stopPropagation();
    }

    /**
     * Esc키를 입력했을 때 처리한다.
     *
     * @param e - 키보드 이벤트
     */
    async onEsc(e: KeyboardEvent): Promise<void> {
        if (this.focusedCell.recordId === null) {
            return;
        }

        this.blurCell();
        e.stopPropagation();
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.rows.forEach((row) => {
            row.destroy();
        });
        this.rows.clear();

        this.groups.forEach((row) => {
            row.destroy();
        });
        this.groups.clear();
        this.groupIndexes = [];
        this.summary.destroy();
        this.summary = null;
        this.table = null;
        this.$body = null;
        this.$freezeCover = null;
    }

    /**
     * 특정 행을 선택하고, 선택사항이 변경되었는지 여부를 가져온다.
     *
     * @param record_id - 선택사항을 변경할 레코드고유값
     */
    async #selectRow(record_id: string, e: PointerEvent | boolean = null): Promise<boolean> {
        const row = this.getRowById(record_id);
        if (row.$getRow().getAttr('disabled') === 'disabled') {
            return false;
        }

        if (this.getTable().getSelection().isSelectable() == true) {
            if (this.getTable().getSelection().isSelected(record_id) == true) {
                if (
                    this.getTable().getSelection().isDeselectable() == true &&
                    this.getTable().getSelection().isSelectable(row.getRecord()) == true
                ) {
                    await this.getTable().getSelection().deselect(record_id);
                    return true;
                }
            } else {
                if (this.getTable().getSelection().isSelectable(row.getRecord()) == true) {
                    await this.getTable().getSelection().select(record_id, e);
                    return true;
                }
            }
        }

        return false;
    }
}
